const freezeDestination = (destination) => Object.freeze({ ...destination });

export const MAIL_TRIAGE_DESTINATIONS = Object.freeze([
  freezeDestination({ key: 'urgent', label: 'Urgent', providerDisplayName: 'URGENT' }),
  freezeDestination({ key: 'financial', label: 'Financial', providerDisplayName: 'FINANCIAL' }),
  freezeDestination({ key: 'for_review', label: 'For review', providerDisplayName: 'FOR REVIEW' }),
]);

export const MAIL_TRIAGE_DESTINATION_OPTIONS = Object.freeze(
  MAIL_TRIAGE_DESTINATIONS.map(({ key, label }) => freezeDestination({ key, label }))
);

export const MAIL_TRIAGE_DESTINATION_KEYS = Object.freeze(
  MAIL_TRIAGE_DESTINATIONS.map(({ key }) => key)
);

export const DESTINATION_LABEL_BY_KEY = Object.freeze(Object.fromEntries(
  MAIL_TRIAGE_DESTINATIONS.map(({ key, label }) => [key, label])
));

export const DESTINATION_KEY_BY_PROVIDER_NAME = Object.freeze(Object.fromEntries(
  MAIL_TRIAGE_DESTINATIONS.map(({ key, providerDisplayName }) => [providerDisplayName, key])
));

const TRIAGE_DESTINATION_KEY_SET = Object.freeze(new Set(MAIL_TRIAGE_DESTINATION_KEYS));

// Membership is exact: a label the app does not own must never mark mail as filed,
// or an arbitrary sender-influenced string could evade triage entirely.
// labels is consulted only as a fallback for rows written before
// emails.triage_destination was populated, where the key survives only there.
//
// `configuredDestinationKeys` (Custom Folders & Smart Email Routing, D-09):
// an optional iterable of every key CURRENTLY authorized for this mailbox --
// the 3 system keys plus any enabled, non-shadowed custom keys. Omitting it
// preserves the original system-only default exactly (existing callers are
// unaffected); passing it extends exact membership to include configured
// custom keys. A custom-looking prefix alone never grants membership -- the
// caller must have resolved the live, owned configuration.
export const isFiledInTriageDestination = (email, configuredDestinationKeys) => {
  if (!email) return false;
  const keySet = configuredDestinationKeys
    ? new Set(configuredDestinationKeys)
    : TRIAGE_DESTINATION_KEY_SET;
  if (keySet.has(email.triageDestination)) return true;
  if (!Array.isArray(email.labels)) return false;
  return email.labels.some((label) => keySet.has(label));
};

// D-02: action type <-> stored destination key conversion, with the D-01
// legacy exception preserved exactly (stored key stays `for_review` even
// though the action type is `move-to-for-review`, matching the PDF's
// differently-spelled action name without touching stored data).
const MOVE_ACTION_PREFIX = 'move-to-';
const LEGACY_FOR_REVIEW_ACTION_TYPE = 'move-to-for-review';

// Exact configured membership, never a prefix match alone: an unconfigured
// or disabled key returns null even though it is shaped like `move-to-*`.
export const destinationKeyForActionType = (actionType, configuredDestinationKeys = []) => {
  if (typeof actionType !== 'string') return null;
  if (actionType === LEGACY_FOR_REVIEW_ACTION_TYPE) {
    return configuredDestinationKeys.includes('for_review') ? 'for_review' : null;
  }
  if (!actionType.startsWith(MOVE_ACTION_PREFIX)) return null;
  const candidateKey = actionType.slice(MOVE_ACTION_PREFIX.length);
  return configuredDestinationKeys.includes(candidateKey) ? candidateKey : null;
};

export const actionTypeForDestinationKey = (destinationKey) => (
  destinationKey === 'for_review' ? LEGACY_FOR_REVIEW_ACTION_TYPE : `${MOVE_ACTION_PREFIX}${destinationKey}`
);
